import { ChartTable } from './ChartTable';
import { niceAxis, niceMax, ticks, visibleLabels } from './scale';
import { useChartWidth } from './useChartWidth';
import { cn } from '@/util/utils';

export interface LineChartProps {
    /** One per point, read as categories: dates across two months are not a number line. */
    labels: string[];
    values: number[];
    format: (value: number) => string;
    title: string;
    /**
     * What `values` are, named in the legend and the hidden table; needed only beside a
     * comparison, which a single line has no use for.
     */
    seriesLabel?: string;
    /** A second, dashed line to read the first against, point for point: the year before. */
    comparison?: { label: string; values: number[] };
    /**
     * Start the value axis at about this rather than at zero, floored to a round step: right for an
     * index, where a zero baseline would leave most of the frame empty, and wrong for a quantity.
     */
    baseline?: number;
    height?: number;
    className?: string;
}

const LEFT = 52;
const RIGHT = 12;
const BOTTOM = 24;
const TOP = 12;

/**
 * The dashboard Sales card's revenue line: value gridlines, a line through categorical points
 * with a soft fill under it, a dot on each. Its viewBox is the card's own width (useChartWidth).
 * Reports › Clients' cycles draw the year before beside it as a dashed `comparison` (ALS-87),
 * with a legend naming the two. Brand intelligence's interest trend, an index, starts its axis
 * just below its lowest point with a `baseline` (ALS-92).
 */
export function LineChart({
    labels,
    values,
    format,
    title,
    seriesLabel = title,
    comparison,
    baseline,
    height = 200,
    className,
}: LineChartProps) {
    // Drawn at the width it is shown at, so its labels stay 11px on a phone.
    const [figure, width] = useChartWidth<HTMLElement>();
    const all = comparison ? [...values, ...comparison.values] : values;
    const hasValues = all.some((value) => value > 0);
    const axis = baseline === undefined ? undefined : niceAxis(baseline, Math.max(...all), 4);
    const bottom = axis ? axis.lo : 0;
    const top = axis ? axis.lo + axis.step * 4 : niceMax(Math.max(0, ...all));
    const gridlines = axis
        ? Array.from({ length: 5 }, (_, i) => axis.lo + axis.step * i)
        : ticks(top, 4, hasValues);
    const shown = visibleLabels(labels, width - LEFT - RIGHT);
    const plot = { width: width - LEFT - RIGHT, height: height - TOP - BOTTOM };
    const x = (i: number) =>
        LEFT + (labels.length > 1 ? (plot.width / (labels.length - 1)) * i : plot.width / 2);
    const y = (value: number) =>
        TOP + plot.height - ((Math.max(bottom, value) - bottom) / (top - bottom)) * plot.height;
    const points = values.map((value, i) => `${x(i)},${y(value)}`);
    const compared = comparison?.values.map((value, i) => `${x(i)},${y(value)}`) ?? [];
    const area = points.length
        ? `M${x(0)},${y(bottom)} L${points.join(' L')} L${x(values.length - 1)},${y(bottom)} Z`
        : '';

    return (
        <figure ref={figure} className={cn('m-0 min-w-0', className)}>
            <svg
                viewBox={`0 0 ${width} ${height}`}
                className="block h-auto w-full"
                role="img"
                aria-label={title}
            >
                {gridlines.map((tick) => (
                    <g key={tick}>
                        <line
                            x1={LEFT}
                            x2={width - RIGHT}
                            y1={y(tick)}
                            y2={y(tick)}
                            className="stroke-border"
                            strokeWidth={1}
                        />
                        <text
                            x={LEFT - 8}
                            y={y(tick) + 4}
                            textAnchor="end"
                            className="fill-muted-foreground text-[11px]"
                        >
                            {format(tick)}
                        </text>
                    </g>
                ))}
                {area ? <path d={area} className="fill-chart-1 opacity-10" /> : null}
                {compared.length ? (
                    <polyline
                        data-series="comparison"
                        points={compared.join(' ')}
                        fill="none"
                        strokeWidth={2}
                        strokeDasharray="5 4"
                        strokeLinejoin="round"
                        strokeLinecap="round"
                        className="stroke-report-5"
                    />
                ) : null}
                {points.length ? (
                    <polyline
                        points={points.join(' ')}
                        fill="none"
                        strokeWidth={2.5}
                        strokeLinejoin="round"
                        strokeLinecap="round"
                        className="stroke-chart-1"
                    />
                ) : null}
                {values.map((value, i) => (
                    <circle
                        key={i}
                        data-point={labels[i]}
                        cx={x(i)}
                        cy={y(value)}
                        r={3.5}
                        className="fill-card stroke-chart-1"
                        strokeWidth={2}
                    />
                ))}
                {/* By position: a short range's cut-offs can share a day, so labels repeat. */}
                {labels.map((label, i) =>
                    shown[i] ? (
                        <text
                            key={i}
                            x={x(i)}
                            y={height - 6}
                            // The ends sit inside the chart rather than half off its edges.
                            textAnchor={
                                i === 0 ? 'start' : i === labels.length - 1 ? 'end' : 'middle'
                            }
                            className="fill-muted-foreground text-[11px]"
                        >
                            {label}
                        </text>
                    ) : null,
                )}
            </svg>
            {comparison ? (
                <ul className="text-muted-foreground mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
                    <li className="inline-flex items-center gap-1.5">
                        <i aria-hidden className="bg-chart-1 inline-block h-0.5 w-4 rounded-full" />
                        {seriesLabel}
                    </li>
                    <li className="inline-flex items-center gap-1.5">
                        <i
                            aria-hidden
                            className="border-report-5 inline-block w-4 border-t-2 border-dashed"
                        />
                        {comparison.label}
                    </li>
                </ul>
            ) : null}
            <ChartTable
                caption={title}
                columns={comparison ? ['', seriesLabel, comparison.label] : ['', title]}
                rows={labels.map((label, i) => ({
                    label,
                    cells: comparison
                        ? [format(values[i] ?? 0), format(comparison.values[i] ?? 0)]
                        : [format(values[i] ?? 0)],
                }))}
            />
        </figure>
    );
}
